import React, { FunctionComponent, ReactNode, useEffect } from "react";

import { useKeyboardNavigationContext } from "../KeyboardNavigationContext";
import { NavigableComponentProps } from "../types";

export type FocusableItemWrapperProps = NavigableComponentProps & {
    /** Jestli má wrapper nastavit předané id i do svého DOM elementu */
    exposeId?: boolean;
    /** Potomek, kterého tento wrapper obaluje */
    children: ReactNode;
};

/**
 * Wrapper sloužící k obalení zaměřitelné komponenty a její registraci v navigačním kontextu.
 */
export const FocusableItemWrapper: FunctionComponent<FocusableItemWrapperProps> = ({ id, exposeId = false, focusable, children }) => {
    const { addItem, removeItem, setItemFocusability, focusItem, focusNextItem } = useKeyboardNavigationContext();

    /**
     * useEffect pro přidání a odebrání komponenty z navigačního kontextu.
     * Komponentu přidáváme a odebíráme jen pokud známe její id,
     * v opačném případě bychom se neměli při změně zaměření čeho chytit.
     */
    useEffect(() => {
        addItem(id);
        return () => removeItem(id);
    }, [id, removeItem, addItem]);

    /**
     * useEffect pro nastavení stavu zaměřitelnosti komponenty.
     * Tento stav znamená, zda je možné komponentu označit při navigaci
     * klávesnicí a může například přímo reflektovat její viditelnost.
     */
    useEffect(() => {
        setItemFocusability(id, focusable);
    }, [id, setItemFocusability, focusable]);

    return (
        <div
            id={exposeId ? id : undefined}
            className="focusable-item-wrapper"
            onMouseDown={() => {
                focusItem(id);
            }}
            onKeyDown={focusNextItem}
            onFocus={(event) => {
                focusItem(id);
                event.stopPropagation();
            }}
            onBlur={(event) => {
                focusItem(null);
                event.stopPropagation();
            }}
        >
            {children}
        </div>
    );
};
